import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Flame, Trophy, Crown, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSeo from "../lib/useSeo";

export default function StatsPage() {
    useSeo({
        title: "My Reading",
        description:
            "Your sovereign ledger — every page turned, every chapter sealed, every reading streak on EIGOO Books Hub.",
    });
    const { user } = useAuth();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/stats/me").then(({ data }) => setStats(data));
    }, []);

    if (!stats)
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="eyebrow text-[#D4AF37] animate-pulse">Polishing the ledger…</div>
            </div>
        );

    const tiles = [
        {
            icon: BookOpen,
            label: "Pages Read",
            value: stats.pages_read,
            sub: "Across all volumes",
        },
        {
            icon: Trophy,
            label: "Chapters Completed",
            value: stats.chapters_completed,
            sub: "Sealed in gold",
        },
        {
            icon: Flame,
            label: "Reading Streak",
            value: `${stats.streak} ${stats.streak === 1 ? "day" : "days"}`,
            sub: stats.streak > 0 ? "The flame burns" : "Light a candle today",
        },
    ];

    return (
        <main data-testid="stats-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="mb-14">
                <div className="eyebrow mb-3">My Reading</div>
                <h1 className="font-serif text-5xl md:text-6xl tracking-tight">
                    Welcome back,{" "}
                    <span className="text-gold-gradient italic">{user?.name}</span>
                </h1>
                <p className="text-[#A1A1AA] mt-4 max-w-xl">
                    Your sovereign ledger — every page turned, every chapter sealed.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                {tiles.map((t, i) => (
                    <motion.div
                        key={t.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        data-testid={`stat-tile-${t.label.toLowerCase().replace(/ /g, "-")}`}
                        className="frame-gold p-8"
                    >
                        <t.icon className="w-6 h-6 text-[#D4AF37] mb-6" />
                        <div className="font-serif text-6xl text-gold-gradient">{t.value}</div>
                        <div className="eyebrow mt-3 text-[10px]">{t.label}</div>
                        <div className="text-[#A1A1AA] text-sm mt-1">{t.sub}</div>
                    </motion.div>
                ))}
            </div>

            <div className="mb-8 flex items-end justify-between">
                <div>
                    <div className="eyebrow mb-2">Recent activity</div>
                    <h2 className="font-serif text-3xl">Continue reading</h2>
                </div>
            </div>

            {stats.recent.length === 0 ? (
                <div
                    data-testid="stats-empty"
                    className="frame-gold p-12 text-center"
                >
                    <Crown className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
                    <p className="font-serif italic text-2xl text-[#E6D59A]">
                        Your story has not yet begun.
                    </p>
                    <Link
                        to="/comic/sovereign-commander"
                        data-testid="empty-cta"
                        className="btn-gold mt-8 inline-flex"
                    >
                        Open Sovereign Commander
                    </Link>
                </div>
            ) : (
                <div className="space-y-2">
                    {stats.recent.map((r, i) => (
                        <motion.div
                            key={r.chapter_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                            data-testid={`recent-${i}`}
                        >
                            <Link
                                to={`/read/${r.chapter_id}`}
                                className="flex items-center gap-6 p-4 frame-gold !p-4 hover:!border-[#D4AF37]/50"
                            >
                                <div
                                    className="w-16 h-20 bg-cover bg-center flex-shrink-0"
                                    style={{ backgroundImage: `url(${r.cover_url})` }}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="eyebrow text-[10px]">{r.comic_title}</div>
                                    <div className="font-serif text-xl text-white">
                                        {r.chapter_title}
                                    </div>
                                    <div className="text-xs text-[#A1A1AA] mt-1">
                                        {r.completed
                                            ? "Completed · sealed in gold"
                                            : `Paused on page ${r.page_index + 1}`}
                                    </div>
                                </div>
                                <ArrowRight className="w-5 h-5 text-[#D4AF37]" />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            )}
        </main>
    );
}
